// src/components/ErrorBoundary.jsx
import React from 'react';

const isChunkError = (error) => {
  const msg = error?.message || '';
  // Standard Vite/webpack chunk load failures
  if (error?.name === 'ChunkLoadError') return true;
  if (msg.includes('Failed to fetch dynamically imported module')) return true;
  if (msg.includes('Importing a module script failed')) return true;
  if (msg.includes('Loading chunk') || msg.includes('Loading CSS chunk')) return true;
  // Firebase SPA fallback: serves index.html for missing chunks when SW isn't
  // active yet → browser tries to parse HTML as JS → SyntaxError with '<'
  if (error instanceof SyntaxError && msg.includes('<')) return true;
  if (msg.includes("Unexpected token '<'") || msg.includes("expected expression, got '<'")) return true;
  return false;
};

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    this.setState({ errorInfo });
    console.error('[ErrorBoundary]', error, errorInfo?.componentStack);

    // Chunk load error = stale deploy. Auto-reload — shares the same 5-second
    // anti-loop key as the vite:preloadError handler and the SW STALE_CHUNK handler.
    if (isChunkError(error)) {
      const last = parseInt(sessionStorage.getItem('chunk_reload_at') || '0', 10);
      if (Date.now() - last > 5_000) {
        sessionStorage.setItem('chunk_reload_at', String(Date.now()));
        window.location.reload();
      }
    }
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });
  };

  render() {
    if (this.state.hasError) {
      if (isChunkError(this.state.error)) {
        // Stale chunk after a new deploy — show a clear "reloading" screen.
        // componentDidCatch will have already triggered window.location.reload()
        // unless the 5-second cooldown blocked it, in which case the user can
        // click the button below.
        return (
          <div className="min-h-screen flex items-center justify-center bg-zinc-100 dark:bg-zinc-950">
            <div className="max-w-sm w-full bg-white dark:bg-zinc-900 rounded-lg shadow-md p-6 text-center">
              <div className="flex items-center justify-center w-12 h-12 mx-auto bg-primary-50 dark:bg-primary-950 rounded-full">
                <svg className="w-6 h-6 text-primary-500 animate-spin" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                </svg>
              </div>
              <h3 className="mt-4 text-base font-semibold text-zinc-900 dark:text-zinc-100">App update detected</h3>
              <p className="mt-1 text-sm text-zinc-500 dark:text-zinc-400">Reloading to get the latest version&hellip;</p>
              <button
                className="mt-4 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700"
                onClick={() => window.location.reload()}
              >
                Reload Now
              </button>
            </div>
          </div>
        );
      }
      return (
        <div className="min-h-screen flex items-center justify-center bg-zinc-100 dark:bg-zinc-950">
          <div className="max-w-md w-full bg-white dark:bg-zinc-900 rounded-lg shadow-md p-6">
            <div className="flex items-center justify-center w-12 h-12 mx-auto bg-zinc-100 dark:bg-zinc-800 rounded-full">
              <svg className="w-6 h-6 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.268 18.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            </div>
            <div className="mt-4 text-center">
              <h3 className="text-lg font-medium text-zinc-900 dark:text-zinc-100">Something went wrong</h3>
              <p className="mt-2 text-sm text-zinc-500 dark:text-zinc-400">
                We're sorry, but something unexpected happened. Please try refreshing the page.
              </p>
              <div className="mt-4 flex gap-3 justify-center">
                <button
                  className="inline-flex items-center px-4 py-2 border border-zinc-300 dark:border-zinc-600 text-sm font-medium rounded-md text-zinc-700 dark:text-zinc-200 bg-white dark:bg-zinc-800 hover:bg-zinc-50 dark:hover:bg-zinc-700"
                  onClick={this.handleReset}
                >
                  Try Again
                </button>
                <button
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-red-600 hover:bg-red-700"
                  onClick={() => window.location.reload()}
                >
                  Refresh Page
                </button>
              </div>
            </div>
            {import.meta.env.DEV && (
              <details className="mt-4">
                <summary className="text-sm text-gray-500 cursor-pointer">Error Details</summary>
                <pre className="mt-2 text-xs text-red-600 bg-zinc-50 p-2 rounded overflow-auto">
                  {this.state.error?.toString()}
                  {'\n'}
                  {this.state.errorInfo?.componentStack}
                </pre>
              </details>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
